<template>
  <div>
    <b-container>
      <validation-observer v-slot="{ handleSubmit }">
        <b-form @submit.stop.prevent="handleSubmit(next)" autocomplete="off">
          <b-card>
            <template #header>
              <h6 class="mb-0">Continuidad de la póliza</h6>
            </template>

            <div class="continuidad-texto mt-3 mb-3 p-3 bg-light rounded">
              <p class="small mb-2">
                Previa evaluación de la aseguradora, se otorga continuidad de amparo de otras pólizas de salud o
                contratos de medicina prepagada, siempre y cuando las coberturas sean equivalentes a las ofrecidas en
                este seguro, para lo cual se requiere certificación de la anterior aseguradora con respecto a
                coberturas, preexistencias, antigüedad y utilizaciones.
              </p>
              <p class="small font-weight-bold mb-0 text-uppercase">
                Si solicita continuidad de otra póliza de salud o contrato de medicina prepagada por favor indique, por
                cada persona asegurar:
              </p>
            </div>

            <p class="small font-weight-bold mt-3">Detalle por solicitante</p>

            <div class="detalle-por-solicitante">
              <b-card
                v-for="(item, index) in form.continuidadDetalle"
                :key="'cont-' + index"
                class="mb-3 continuidad-solicitante-card"
              >
                <template #header>
                  <h6 class="mb-0">{{ item.nombreSolicitante || "Solicitante" }}</h6>
                </template>

                <validation-provider
                  :name="'continuidad-solicita-' + index"
                  rules="required"
                  v-slot="validationContext"
                >
                  <b-form-group
                    :id="'solicitaContinuidad-' + index + '-group'"
                    label="¿Solicita continuidad de otra póliza o contrato para esta persona?"
                    :label-for="'solicitaContinuidad-' + index"
                  >
                    <b-form-radio-group
                      :id="'solicitaContinuidad-' + index"
                      :name="'solicitaContinuidad-' + index"
                      v-model="item.solicitaContinuidad"
                      :options="control.TIPOS_SINO"
                      :state="radioValidationState(validationContext)"
                      :aria-describedby="'solicitaContinuidad-' + index + '-feedback'"
                    />
                    <b-form-invalid-feedback
                      :id="'solicitaContinuidad-' + index + '-feedback'"
                      :state="radioValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <template v-if="item.solicitaContinuidad === 'S'">
                  <b-card no-body class="border continuidad-inner">
                    <b-card-header
                      class="d-flex flex-column flex-sm-row align-items-start align-items-sm-center
                             justify-content-between py-2 continuidad-inner-header bg-light"
                    >
                      <span class="font-weight-medium">Datos de la póliza o contrato anterior</span>
                      <b-button
                        pill
                        variant="outline-danger"
                        size="sm"
                        class="align-self-stretch align-self-sm-auto mt-2 mt-sm-0"
                        @click="borrarDatosContinuidad(item)"
                      >
                        Borrar datos
                      </b-button>
                    </b-card-header>
                    <b-card-body class="pt-3 pb-3">
                      <b-row>
                        <b-col cols="12" md="6">
                          <TextComponent
                            :form="item"
                            name="compania"
                            label="Compañía"
                            type="text"
                            rules="required|max:200"
                          />
                        </b-col>
                        <b-col cols="12" md="6">
                          <TextComponent :form="item" name="plan" label="Plan" type="text" rules="required|max:200" />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            type="date"
                            :form="item"
                            name="fechaInicio"
                            label="Fecha de Inicio"
                            rules="required|date_min:01/01/1900,DD/MM/YYYY|date_max:today,YYYY-MM-DD"
                          />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            :form="item"
                            name="numPolizaContrato"
                            label="Nº de Póliza o Contrato"
                            type="text"
                            rules="required|max:80"
                          />
                        </b-col>
                        <b-col cols="12" md="6" lg="4">
                          <TextComponent
                            type="date"
                            :form="item"
                            :rules="`date_min:${formatDate(
                              (item && item.fechaInicio) || '',
                              'YYYY-MM-DD',
                              'DD/MM/YYYY'
                            )},DD/MM/YYYY`"
                            name="fechaVencimiento"
                            label="Fecha Vencimiento (opcional)"
                          />
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </template>

                <p v-else-if="item.solicitaContinuidad === 'N'" class="text-muted small mb-0">
                  No se registrará continuidad para esta persona.
                </p>
              </b-card>
            </div>

            <b-row align-h="end" class="mt-4">
              <b-col md="4" cols="4" style="display: flex; justify-content: flex-end">
                <b-button @click="previous" variant="secondary">Anterior</b-button>
                <b-button type="submit" variant="primary">Siguiente</b-button>
              </b-col>
            </b-row>
          </b-card>
        </b-form>
      </validation-observer>
    </b-container>
  </div>
</template>

<script>
import TextComponent from "@/components/controls/TextComponent.vue";
import { TIPOS_SINO } from "@/data/colombia/salud/Selects";
import { formatDate } from "../../../../helpers/dateHelper";

function nombreCompletoPersona(persona) {
  return [persona.name, persona.lastName, persona.secondLastName]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
}

function filaTieneDatosContinuidad(row) {
  if (!row) return false;
  return ["compania", "plan", "fechaInicio", "numPolizaContrato", "fechaVencimiento"].some(
    (k) => (row[k] || "").toString().trim() !== ""
  );
}

export default {
  name: "FormColombiaSalud06",
  components: { TextComponent },
  data() {
    return {
      control: { TIPOS_SINO },
      form: {},
    };
  },
  mounted() {
    this.$store.dispatch("app/loading", true);
    const { form } = this.$store.state.wizard;
    if (form === undefined || form === null || form === "") {
      this.$router.push("/form/colombia/salud/");
      return;
    }
    this.form = form;
    if (this.form.nombreTomadorPoliza === undefined) this.form.nombreTomadorPoliza = "";
    if (this.form.fechaSeguro === undefined) this.form.fechaSeguro = "";
    const personasConDatos = this.form.dependientes || [];
    const existing =
      this.form.continuidadDetalle && Array.isArray(this.form.continuidadDetalle) ? this.form.continuidadDetalle : [];

    this.form.continuidadDetalle = personasConDatos.map((persona, i) => {
      const ex = existing[i] || {};
      const tieneDatos = filaTieneDatosContinuidad(ex);
      const solicita =
        ex.solicitaContinuidad === "S" || ex.solicitaContinuidad === "N"
          ? ex.solicitaContinuidad
          : tieneDatos
          ? "S"
          : "N";
      return {
        nombreSolicitante: nombreCompletoPersona(persona) || (persona.name || "").trim() || "",
        solicitaContinuidad: solicita,
        compania: solicita === "S" ? (ex.compania || "").trim() : "",
        plan: solicita === "S" ? (ex.plan || "").trim() : "",
        fechaInicio: solicita === "S" ? (ex.fechaInicio || "").trim() : "",
        numPolizaContrato: solicita === "S" ? (ex.numPolizaContrato || "").trim() : "",
        fechaVencimiento: solicita === "S" ? (ex.fechaVencimiento || "").trim() : "",
      };
    });
    this.$store.dispatch("app/loading", false);
  },
  watch: {
    "form.continuidadDetalle": {
      deep: true,
      handler(rows) {
        (rows || []).forEach((item) => {
          if (item && item.solicitaContinuidad === "N") {
            this.limpiarCamposDetalle(item);
          }
        });
      },
    },
  },
  methods: {
    formatDate,
    radioValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
    limpiarCamposDetalle(item) {
      if (!item) return;
      item.compania = "";
      item.plan = "";
      item.fechaInicio = "";
      item.numPolizaContrato = "";
      item.fechaVencimiento = "";
    },
    borrarDatosContinuidad(item) {
      if (!item) return;
      this.limpiarCamposDetalle(item);
    },
    previous() {
      this.$store.dispatch("wizard/save", { index: 4, form: this.form }).then(() => {
        if (this.form.actionCode.includes("CO203")) {
          if (this.form.saltoForm04 === true) {
            this.form.saltoForm04 = false;
            this.$router.push("/form/colombia-salud-3.html");
          } else {
            this.$router.push("/form/colombia-salud-4.html");
          }
        } else {
          this.$router.push("/form/colombia-salud-5.html");
        }
      });
    },
    next() {
      this.$store
        .dispatch("wizard/save", { index: 5, form: this.form })
        .then(() => this.$router.push("/form/colombia-salud-7.html"));
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
}
.continuidad-solicitante-card .continuidad-inner {
  border-radius: 0.35rem;
}
.continuidad-inner-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.continuidad-solicitante-card .card-header {
  background-color: rgba(0, 39, 118, 0.08);
  font-weight: 600;
}
.continuidad-solicitante-card .card-header h6 {
  color: var(--one);
}
</style>
